// Popular.tsx
import React, { useEffect, useRef, useState } from "react";
import styles from "./Popular.module.css";
import { useSwipeCarousel } from "../utils/useSwipeCarousel";
import { usePopular } from "../hooks/usePopular";

type PopularProps = {
    intervalMs?: number; // default 3000
};

function usePrefersReducedMotion() {
    const [reduced, setReduced] = useState(false);

    useEffect(() => {
        const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
        if (!mq) return;
        const onChange = () => setReduced(mq.matches);
        onChange();

        // Safari < 14 fallback
        if (mq.addEventListener) mq.addEventListener("change", onChange);
        else mq.addListener(onChange);

        return () => {
            if (mq.removeEventListener) mq.removeEventListener("change", onChange);
            else mq.removeListener(onChange);
        };
    }, []);

    return reduced;
}

export const Popular: React.FC<PopularProps> = ({
    intervalMs = 3000
}) => {

    const prefersReducedMotion = usePrefersReducedMotion();
    const {events} = usePopular();
    const [index, setIndex] = useState(0);
    const [isPaused, setPaused] = useState(false);
    const timerRef = useRef<number | null>(null);

    const clampIndex = (i: number) => {
        if (!events || events.length === 0) return 0;
        return (i + events!.length) % events!.length;
    };

    const go = (dir: number) => setIndex((i) => clampIndex(i + dir));
    const goTo = (i: number) => setIndex(clampIndex(i));

    const stop = () => {
        if (timerRef.current) window.clearInterval(timerRef.current);
        timerRef.current = null;
    };

    const swipe = useSwipeCarousel({
        onSwipeLeft: () => go(1),     // swipe left -> next slide
        onSwipeRight: () => go(-1),   // swipe right -> previous slide
        onSwipeStart: () => setPaused(true),
        onSwipeEnd: () => setPaused(false),
        thresholdPx: 40,
        restraintPx: 80,
    });

    const start = () => {
        if (!events || events.length == 0) return;
        if (prefersReducedMotion) return;
        stop();
        timerRef.current = window.setInterval(() => {
            setIndex((i) => clampIndex(i + 1));
        }, intervalMs);
    };

    useEffect(() => {
        if (isPaused) stop();
        else start();
        return stop;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPaused, intervalMs, prefersReducedMotion, events]);

    // Keep index valid if items change
    useEffect(() => {
        setIndex((i) => clampIndex(i));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [events]);

    if (!events || events.length == 0) return null;

    return (
        <section className={styles.popular} aria-label="Populaire en ce moment">
            <div className={styles.popular__header}>
                <h2 className={styles.popular__title}>Populaire en ce moment</h2>

                {events && events.length > 1 && (
                    <div className={styles.popular__controls} aria-label="Navigation du carrousel">
                        <button
                            type="button"
                            className={styles.popular__btn}
                            onClick={() => go(-1)}
                            aria-label="Précédent"
                        >
                            ‹
                        </button>
                        <button
                            type="button"
                            className={styles.popular__btn}
                            onClick={() => go(1)}
                            aria-label="Suivant"
                        >
                            ›
                        </button>
                    </div>
                )}
            </div>

            <div
                className={styles.popular__carousel}
                onMouseEnter={() => setPaused(true)}
                onMouseLeave={() => setPaused(false)}
                onFocus={() => setPaused(true)}
                onBlur={() => setPaused(false)}
            >
                <div className={styles.popular__gestureLayer} {...swipe} />

                {/* slides */}
                {events && events.map((it, i) => (
                    <a
                        key={it.id}
                        href={"/details/" + it.id}
                        className={[styles.popular__slide, i === index ? styles.isActive : ""].join(' ')}
                        style={{ backgroundImage: `url(${it.imageUrl})` }}
                        aria-hidden={i !== index}
                        tabIndex={i === index ? 0 : -1}
                    >
                        <div className={styles.popular__overlay} />

                        <div className={styles.popular__content}>
                            <div className={styles.popular__topRow}>
                                {it.categories && it.categories.map(c => (<span className={styles.popular__pill}>{c.name}</span>))}
                            </div>

                            <div className={styles.popular__headline}>{it.title}</div>

                            <div className={styles.popular__meta}>
                                {it.locationName && <span>{it.locationName}</span>}
                                {it.locationName && it.price && <span className={styles.popular__dot}>•</span>}
                                {it.price === '0' && <span>Gratuit</span>}
                                {it.price && it.price != '0' && <span>{it.price}</span>}
                                {(it.locationName || it.price) && it.date && <span className={styles.popular__dot}>•</span>}
                                {it.date && <span>{it.date}</span>}
                            </div>

                            <div className={styles.popular__cta}>Voir les détails →</div>
                        </div>
                    </a>
                ))}
            </div>

            {
                events && events.length > 1 && (
                    <div className={styles.popular__dots} aria-label="Choisir un élément populaire">
                        {events.map((it, i) => (
                            <button
                                key={it.id}
                                type="button"
                                className={[styles.popular__dotBtn, i === index ? styles.isActive : ""].join(' ')}
                                onClick={() => goTo(i)}
                                aria-label={`Aller à l’élément ${i + 1}`}
                                aria-current={i === index ? "true" : undefined}
                            />
                        ))}
                    </div>
                )
            }
        </section >
    );
};
